import type {
  CompanyMeta,
  CompanyGroupRef,
  CompanyGroupInfo,
  CompanyId,
  CustomerYearData,
  ActivityEventInput,
  ActivitySession,
  ActivityEvent,
  ActivitySummary,
  AppSettingsResponse,
  AppSettingsPatch,
  CarbonRouting,
  AccessRequestCreate,
  AccessRequestApprovalView,
  AccessRequestAdminRow,
  AccessRequestStatus,
} from '@igf-minar/shared'
import { getLocale } from '@/i18n/locale'
import { messagesFor } from '@/i18n/messages'
import { commonMessages } from '@/i18n/common'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

const TOKEN_KEY = 'igf-token'
const BASIC_KEY = 'igf-basic-auth'
const SESSION_KEY = 'igf-session'

export type UserRole = 'superadmin' | 'admin' | 'user'

export interface AuthUser {
  id: string
  email: string
  role: UserRole
  companyIds: string[] | '*'
  mustChangePassword: boolean
  claims?: { displayName?: string | null }
}

export interface PhoneLoginUser {
  index: number
  name: string | null
  company: string | null
}

export interface AdminUser {
  id: string
  email: string
  displayName: string | null
  role: UserRole
  isActive: boolean
  mustChangePassword: boolean
  lastLoginAt: string | null
  createdAt: string
  authProvider: string
  companyIds: string[] | '*'
  linkedCompanyIds: string[]
}

export interface AdminCompany {
  id: string
  name: string
  shortName: string
  kennitala: string | null
  dataSource: string
  /** "seed" (known ÍGF customer) or "habilis" (auto-provisioned). Only habilis rows are deletable. */
  source: string
  userCount: number
}

export interface Svr2TestResult {
  /** ok = rows returned; empty = HTTP 200 but no rows; not_configured / fetch_failed = call did not succeed. */
  state: 'ok' | 'empty' | 'not_configured' | 'fetch_failed'
  custId: string
  year: number
  month: number
  rowCount?: number
  elapsedMs?: number
  message: string
}

function getAuthHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) return { Authorization: `Bearer ${token}` }
  const basic = localStorage.getItem(BASIC_KEY)
  if (basic) return { Authorization: `Basic ${basic}` }
  return {}
}

export interface LocationGalleryImage {
  url: string
  contentType?: string | null
  contentLength?: number | null
  collectionId: string
  dateCollected: string
  materialId?: string | null
  materialName: string
  weight: number
  containerId?: string | null
}

export interface AccessAudit {
  summary: { totalGrants: number; usersWithGrants: number; regularUsers: number; maxCompaniesPerUser: number }
  problems: {
    orphanGrantsUserMissing: { userId: string; companyId: string }[]
    grantsToUnknownCompany: { userId: string; companyId: string }[]
    privilegedUsersWithExplicitGrants: { userId: string; email: string; role: string; grants: number }[]
  }
  regularUsers: { email: string; active: boolean; companies: string[] }[]
}

export interface ZeroCollectionsReport {
  generatedAt: string
  months: number
  fromMonth: string
  toMonth: string
  totals: { collections: number; zero: number; empty: number; noAccess: number; other: number; blank: number }
  companies: {
    id: string; name: string; kennitala: string | null
    collections: number; zero: number; empty: number; noAccess: number; other: number; blank: number; zeroPct: number
  }[]
  remarks: { company: string; date: string; location: string; material: string; kind: string; text: string }[]
}

export interface UnknownMaterialsReport {
  generatedAt: string
  cachedMonths: number
  customers: number
  unknownKg: number
  materials: {
    materialId: string
    materialName: string
    materialGroup: string | null
    kg: number
    collections: number
    firstSeen: string
    lastSeen: string
    companies: { kennitala: string; name: string; kg: number; collections: number }[]
  }[]
}

export interface Svr2CustomerHit {
  kennitala: string
  name: string
  nameAlias: string | null
  isCompany: boolean
  companyId: string | null
}

export interface ApiIssues {
  formErrors: string[]
  fieldErrors: Record<string, string[]>
}

export class ApiError extends Error {
  status: number
  issues?: ApiIssues
  constructor(message: string, status: number, issues?: ApiIssues) {
    super(message)
    this.status = status
    this.issues = issues
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getLocale(),
      ...getAuthHeader(),
      ...(init.headers ?? {}),
    },
  })

  if (!res.ok) {
    let message = res.statusText
    let issues: ApiIssues | undefined
    try {
      const body = await res.json()
      if (body?.message) message = body.message
      if (body?.issues) issues = body.issues
    } catch {
      // ignore
    }
    throw new ApiError(message, res.status, issues)
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  try {
    return JSON.parse(text) as T
  } catch {
    // CloudFront rewrites API 403/404 responses to the SPA's index.html (HTML,
    // status 200), so a JSON endpoint can come back as an HTML document. Detect
    // that fallback and surface a real "not found" instead of a raw JSON crash.
    if (text.trimStart().startsWith('<')) {
      throw new ApiError(messagesFor(commonMessages).noDataOrAccess, 404)
    }
    throw new ApiError(messagesFor(commonMessages).invalidServerResponse, res.status)
  }
}

async function requestText(path: string): Promise<string> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Accept-Language': getLocale(), ...getAuthHeader() },
  })
  if (!res.ok) {
    let message = res.statusText
    try {
      const body = await res.json()
      if (body?.message) message = body.message
    } catch {
      // ignore
    }
    throw new ApiError(message, res.status)
  }
  return res.text()
}

export type WorkSide = 'customer' | 'team'
export type WorkKind = 'virkni' | 'bilun' | 'smamunir' | 'utlit' | 'spurning'
export type WorkStatus = 'ny' | 'i_skodun' | 'samthykkt' | 'i_vinnslu' | 'lokid' | 'hafnad'
export type WorkReviewState = 'pending' | 'decide' | 'go' | 'nogo'

export interface WorkItem {
  id: string
  number: number
  title: string
  description: string
  kind: WorkKind
  priority: 1 | 2 | 3
  status: WorkStatus
  where: string | null
  rank: number
  createdByEmail: string
  createdByName: string
  createdBySide: WorkSide
  awaitingSide: WorkSide
  activityAt: string
  createdAt: string
  messageCount: number
  attachmentCount: number
  unread: boolean
  review: WorkReviewState | null
}

export interface WorkReview {
  summary: string
  size: 'S' | 'M' | 'L'
  risk: string
  proposal: string
  replyGo: string
  replyNoGo: string
  reviewedBy: string
  reviewedAt: string
  stale: boolean
  decision: 'go' | 'nogo' | null
  decidedByName: string | null
  decidedAt: string | null
  decisionNote: string | null
  startedAt: string | null
  prUrl: string | null
}

export interface WorkMessage {
  id: string
  authorEmail: string
  authorName: string
  side: WorkSide
  body: string
  createdAt: string
}

export interface WorkAttachment {
  id: string
  dataUrl: string
  createdAt: string
}

export interface WorkDetail {
  item: WorkItem
  messages: WorkMessage[]
  attachments: WorkAttachment[]
  review: WorkReview | null
}

export interface WorkAgentStatus {
  hasKey: boolean
  createdAt: string | null
  lastSeenAt: string | null
}

export type WorkPatch = Partial<{ title: string; description: string; kind: WorkKind; priority: number; status: WorkStatus; where: string }>

export function setBasicAuth(user: string, password: string) {
  const encoded = btoa(`${user}:${password}`)
  localStorage.setItem(BASIC_KEY, encoded)
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.removeItem(BASIC_KEY)
}

export function setSessionId(id: string) {
  localStorage.setItem(SESSION_KEY, id)
}

export function getSessionId(): string | null {
  return localStorage.getItem(SESSION_KEY)
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(BASIC_KEY)
  localStorage.removeItem(SESSION_KEY)
}

export function hasAuth(): boolean {
  return Boolean(localStorage.getItem(TOKEN_KEY) || localStorage.getItem(BASIC_KEY))
}

export const api = {
  health: () => request<{ status: string; uptime: number }>('/healthz'),
  ready: () => request<{ status: string; db: string; timestamp: string }>('/readyz'),

  authMethods: () =>
    request<{ password: boolean; phone: boolean; accessRequests: boolean; mailpit: boolean }>('/api/auth/methods'),
  mailpitSession: () => request<{ url: string }>('/api/admin/mailpit/session', { method: 'POST' }),
  loginPhone: (phone: string) =>
    request<{ loginId: string; securityNumber: string; expiresAt: string | null }>('/api/auth/login/phone', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    }),
  loginPhoneStatus: (loginId: string) =>
    request<{
      status: 'pending' | 'approved' | 'rejected' | 'expired' | 'failed'
      users?: PhoneLoginUser[]
      message?: string | null
    }>(`/api/auth/login/status?loginId=${encodeURIComponent(loginId)}`),
  loginPhoneConfirm: (loginId: string, userIndex?: number) =>
    request<{ token: string; user: AuthUser; sessionId: string | null }>('/api/auth/login/confirm', {
      method: 'POST',
      body: JSON.stringify({ loginId, userIndex }),
    }),

  login: (email: string, password: string) =>
    // sessionId is null when the server couldn't open an analytics session —
    // login still succeeds without one.
    request<{ token: string; user: AuthUser; sessionId: string | null }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () =>
    request<{ ok: true }>('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ sessionId: getSessionId() }),
    }),
  me: () => request<{ user: AuthUser }>('/api/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true; token: string }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  // Always resolves ok (the server never reveals whether the email exists).
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),

  // Per-user preferences (sidebar layout, dashboard config). Synced across
  // devices via user_settings.preferences.
  getSettings: () => request<{ preferences: Record<string, unknown> }>('/api/me/settings'),
  saveSettings: (preferences: Record<string, unknown>) =>
    request<{ ok: true }>('/api/me/settings', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    }),

  companies: () => request<{ companies: CompanyMeta[] }>('/api/companies'),
  company: (id: string) => request<{ company: CompanyMeta; group?: CompanyGroupRef | null }>(`/api/companies/${id}`),
  companyData: (id: string, year: number) =>
    request<{ company: CompanyMeta; year: number; data: CustomerYearData; carbonMethodology?: string | null; carbonFactors?: unknown }>(
      `/api/companies/${id}/data?year=${year}`
    ),
  companyGroupData: (id: string, year: number) =>
    request<{ company: CompanyMeta; group: CompanyGroupInfo; year: number; data: CustomerYearData; carbonMethodology?: string | null; carbonFactors?: unknown }>(
      `/api/companies/${id}/group?year=${year}`
    ),
  companyLocationImages: (id: string, location: string, year: number, group = false) =>
    request<{ location: string; year: number; images: LocationGalleryImage[]; partial?: boolean }>(
      `/api/companies/${id}/locations/images?location=${encodeURIComponent(location)}&year=${year}${group ? '&group=true' : ''}`
    ),
  sendLocationFeedback: (
    id: string,
    body: {
      location: string
      year: number
      comment: string
      images: { url: string; dateCollected: string; materialName: string; weight: number; collectionId: string; containerId?: string | null }[]
    }
  ) =>
    request<{ ok: true; sent: number }>(`/api/companies/${id}/locations/feedback`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Public "apply for access" flow — no auth, callable from the login page.
  accessRequests: {
    create: (body: AccessRequestCreate) =>
      request<{ ok: true; approverEmailSent?: boolean; alreadyPending?: boolean }>('/api/access-requests', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    getApproval: (token: string) =>
      request<{ request: AccessRequestApprovalView }>(
        `/api/access-requests/approval?token=${encodeURIComponent(token)}`
      ),
    decide: (token: string, decision: 'approve' | 'reject', note?: string) =>
      request<{ ok: true; status: AccessRequestStatus }>('/api/access-requests/approval', {
        method: 'POST',
        body: JSON.stringify({ token, decision, note }),
      }),
  },

  admin: {
    svr2Customers: {
      search: (q: string, individuals = false) =>
        request<{ customers: Svr2CustomerHit[]; total: number }>(
          `/api/admin/svr2-customers?q=${encodeURIComponent(q)}${individuals ? '&individuals=true' : ''}`
        ),
      open: (kennitala: string) =>
        request<{ companyId: string }>('/api/admin/svr2-customers/open', {
          method: 'POST',
          body: JSON.stringify({ kennitala }),
        }),
      sync: () =>
        request<{ total: number; companies: number; individuals: number; added: number; updated: number }>(
          '/api/admin/svr2-customers/sync',
          { method: 'POST' }
        ),
    },
    accessAudit: () => request<AccessAudit>('/api/admin/access-audit'),
    zeroCollections: {
      get: () => request<{ report: ZeroCollectionsReport | null }>('/api/admin/reports/zero-collections'),
      refresh: () => request<{ report: ZeroCollectionsReport }>('/api/admin/reports/zero-collections/refresh', { method: 'POST' }),
    },
    unknownMaterials: {
      get: () => request<{ report: UnknownMaterialsReport | null }>('/api/admin/reports/unknown-materials'),
      refresh: () => request<{ report: UnknownMaterialsReport }>('/api/admin/reports/unknown-materials/refresh', { method: 'POST' }),
    },
    listAccessRequests: (status?: string) =>
      request<{ requests: AccessRequestAdminRow[] }>(
        `/api/admin/access-requests${status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : ''}`
      ),
    completeAccessRequest: (id: string) =>
      request<{ ok: true; companyId: string; email: string; tempPassword?: string; existingUser?: boolean; emailSent: boolean }>(
        `/api/admin/access-requests/${id}/complete`,
        { method: 'POST' }
      ),
    cancelAccessRequest: (id: string) =>
      request<{ ok: true }>(`/api/admin/access-requests/${id}/cancel`, { method: 'POST' }),
    listUsers: () => request<{ users: AdminUser[] }>('/api/admin/users'),
    createUser: (body: {
      email: string
      displayName?: string
      password: string
      role: UserRole
      companyIds?: CompanyId[]
    }) => request<{ id: string }>('/api/admin/users', { method: 'POST', body: JSON.stringify(body) }),
    updateUser: (
      id: string,
      body: { displayName?: string; isActive?: boolean; role?: UserRole; password?: string }
    ) => request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    setAccess: (id: string, companyIds: CompanyId[]) =>
      request<{ ok: true }>(`/api/admin/users/${id}/access`, {
        method: 'PUT',
        body: JSON.stringify({ companyIds }),
      }),
    deleteUser: (id: string) =>
      request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),
    sendCredentials: (id: string) =>
      request<{ email: string; tempPassword: string; loginUrl: string; emailSent: boolean }>(
        `/api/admin/users/${id}/send-credentials`,
        { method: 'POST' }
      ),

    settings: {
      get: () => request<AppSettingsResponse>('/api/admin/settings'),
      update: (patch: AppSettingsPatch) =>
        request<{ ok: true; changed: string[] }>('/api/admin/settings', {
          method: 'PUT',
          body: JSON.stringify(patch),
        }),
      // Switches for built-but-unreleased features. Only /settings/hidden calls these.
      getHidden: () =>
        request<{ settings: { key: string; kind: string; label: string; description?: string | null; value: string | null }[] }>(
          '/api/admin/settings/hidden'
        ),
      updateHidden: (patch: Record<string, string>) =>
        request<{ ok: true; changed: string[] }>('/api/admin/settings/hidden', {
          method: 'PUT',
          body: JSON.stringify(patch),
        }),
    },

    // Company registry management (superadmin only).
    companies: {
      list: () => request<{ companies: AdminCompany[] }>('/api/admin/companies'),
      rename: (id: string, body: { name: string; shortName?: string }) =>
        request<{ ok: true }>(`/api/admin/companies/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      remove: (id: string) =>
        request<{ ok: true }>(`/api/admin/companies/${id}`, { method: 'DELETE' }),
      setCarbonRouting: (id: string, routing: CarbonRouting) =>
        request<{ ok: true; routing: CarbonRouting }>(`/api/admin/companies/${id}/carbon-routing`, {
          method: 'PUT',
          body: JSON.stringify(routing),
        }),
    },

    // SVR2 diagnostics (superadmin only).
    svr2: {
      test: (body: { companyId?: string; custId?: string; year: number; month: number }) =>
        request<Svr2TestResult>('/api/admin/svr2/test', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      clearCache: (body: { companyId?: string; custId?: string; year?: number }) =>
        request<{ ok: true; custId: string; year: number | null; deleted: number; message: string }>(
          '/api/admin/svr2/clear-cache',
          { method: 'POST', body: JSON.stringify(body) }
        ),
    },

    activity: {
      sessions: (params: ActivityQuery = {}) =>
        request<{ sessions: ActivitySession[]; total: number }>(
          `/api/admin/activity/sessions${queryString(params)}`
        ),
      session: (id: string) =>
        request<{ session: ActivitySession; events: ActivityEvent[] }>(
          `/api/admin/activity/sessions/${id}`
        ),
      events: (params: ActivityQuery = {}) =>
        request<{ events: ActivityEvent[]; total: number }>(
          `/api/admin/activity/events${queryString(params)}`
        ),
      summary: () => request<ActivitySummary>('/api/admin/activity/summary'),
    },
  },

  work: {
    list: () => request<{ items: WorkItem[]; side: WorkSide }>('/api/work/'),
    badge: () => request<{ count: number }>('/api/work/badge'),
    get: (number: number) => request<WorkDetail>(`/api/work/${number}`),
    create: (body: { title: string; description: string; kind: WorkKind; priority: number; where?: string | null }) =>
      request<WorkDetail>('/api/work/', { method: 'POST', body: JSON.stringify(body) }),
    patch: (number: number, body: WorkPatch) =>
      request<WorkDetail>(`/api/work/${number}`, { method: 'PATCH', body: JSON.stringify(body) }),
    reorder: (numbers: number[]) =>
      request<{ ok: true; count: number }>('/api/work/reorder', { method: 'POST', body: JSON.stringify({ numbers }) }),
    message: (number: number, body: string) =>
      request<WorkDetail>(`/api/work/${number}/messages`, { method: 'POST', body: JSON.stringify({ body }) }),
    attach: (number: number, blob: Blob) =>
      request<WorkDetail>(`/api/work/${number}/attachments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      }),
    removeAttachment: (number: number, id: string) =>
      request<{ ok: true }>(`/api/work/${number}/attachments/${id}`, { method: 'DELETE' }),
    brief: (number: number) => requestText(`/api/work/${number}/brief`),
    decide: (number: number, body: { decision: 'go' | 'nogo'; reply: string; note?: string; done?: boolean }) =>
      request<WorkDetail>(`/api/work/${number}/decision`, { method: 'POST', body: JSON.stringify(body) }),
    agent: () => request<WorkAgentStatus>('/api/work/agent/'),
    createAgentKey: () => request<{ key: string; status: WorkAgentStatus }>('/api/work/agent/key', { method: 'POST' }),
    revokeAgentKey: () => request<WorkAgentStatus>('/api/work/agent/key', { method: 'DELETE' }),
  },

  activity: {
    /**
     * Send a batch of interactions. Fire-and-forget: never throws, and uses
     * `keepalive` so it still flushes while the page is unloading (sendBeacon
     * can't attach the auth header, so keepalive fetch is used instead).
     */
    track: (sessionId: string | null, events: ActivityEventInput[], keepalive = false) => {
      if (events.length === 0) return Promise.resolve()
      return fetch(`${API_BASE}/api/activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ sessionId, events }),
        keepalive,
      }).catch(() => {
        /* analytics is best-effort */
      })
    },
    export: (companyId: string, body: { report: string; format: 'xlsx' | 'pdf'; year: number; group: boolean }) =>
      fetch(`${API_BASE}/api/companies/${companyId}/exports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ sessionId: getSessionId(), ...body }),
        keepalive: true,
      }).catch(() => {}),
  },
}

export interface ActivityQuery {
  userId?: string
  sessionId?: string
  type?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

function queryString(params: ActivityQuery): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  if (entries.length === 0) return ''
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
}
